import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "tabId", "getItem"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState, useEffect, useCallback } from 'react';
import { getAllTabbableIn } from 'ariakit-utils/focus';
import { useId, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useCollectionItem } from '../collection/collection-item.js';
import { useDisclosureState } from '../disclosure/disclosure-state.js';
import { useDisclosureContent } from '../disclosure/disclosure-content.js';
import { useFocusable } from '../focusable/focusable.js';

function getTabId(panels, id) {
  var _panels$items$find;

  if (!id) return;
  return (_panels$items$find = panels.items.find(function (panel) {
    return panel.id === id;
  })) == null ? void 0 : _panels$items$find.tabId;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tab panel element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const state = useTabState();
 * const props = useTabPanel({ state });
 * <TabList state={state}>
 *   <Tab>Tab 1</Tab>
 * </TabList>
 * <Role {...props}>Panel 1</Role>
 * ```
 */


var useTabPanel = createHook(function (_ref) {
  var state = _ref.state,
      tabIdProp = _ref.tabId,
      getItemProp = _ref.getItem,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);
  var id = useId(props.id);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasTabbableChildren = _useState2[0],
      setHasTabbableChildren = _useState2[1];

  useEffect(function () {
    var element = ref.current;
    if (!element) return;
    var tabbable = getAllTabbableIn(element);
    setHasTabbableChildren(!!tabbable.length);
  }, []);
  var getItem = useCallback(function (item) {
    var nextItem = _objectSpread(_objectSpread({}, item), {}, {
      id: id,
      tabId: tabIdProp
    });

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [id, tabIdProp, getItemProp]);
  var tabId = tabIdProp || getTabId(state.panels, id);
  var open = !!tabId && state.selectedId === tabId;
  props = _objectSpread(_objectSpread({
    id: id,
    role: "tabpanel",
    "aria-labelledby": tabId || undefined
  }, props), {}, {
    ref: useForkRef(ref, props.ref)
  });
  var disclosure = useDisclosureState({
    open: open
  });
  props = useFocusable(_objectSpread({
    focusable: !hasTabbableChildren
  }, props));
  props = useDisclosureContent(_objectSpread({
    state: disclosure
  }, props));
  props = useCollectionItem(_objectSpread(_objectSpread({
    state: state.panels
  }, props), {}, {
    getItem: getItem
  }));
  return props;
});
/**
 * A component that renders a tab panel element.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */

var TabPanel = createComponent(function (props) {
  var htmlProps = useTabPanel(props);
  return createElement("div", htmlProps);
});
export { TabPanel, useTabPanel };